import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Home, GitBranch, Stethoscope, Activity, Quote, Users, CreditCard, BookOpen, HelpCircle, Image, Settings, ExternalLink, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/home", label: "Homepage", icon: Home },
  { to: "/admin/approach", label: "Our Approach", icon: GitBranch },
  { to: "/admin/services", label: "Treatments", icon: Stethoscope },
  { to: "/admin/conditions", label: "Conditions", icon: Activity },
  { to: "/admin/testimonials", label: "Patient Stories", icon: Quote },
  { to: "/admin/staff", label: "Staff", icon: Users },
  { to: "/admin/memberships", label: "Memberships", icon: CreditCard },
  { to: "/admin/articles", label: "Education", icon: BookOpen },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/media", label: "Media Library", icon: Image },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export function AdminShell({ children, title, subtitle, actions }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-linen flex">
      <aside className="w-64 shrink-0 bg-espresso text-ivory flex flex-col fixed h-screen">
        <div className="p-7 border-b border-ivory/10">
          <div className="font-serif text-2xl">Psalm 30:2</div>
          <div className="text-[0.6rem] uppercase tracking-[0.3em] text-ivory/50 mt-1">Content Portal</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => {
            const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-7 py-3 text-sm transition-colors ${active ? "bg-ivory/10 text-ivory border-l-2 border-brass" : "text-ivory/60 hover:text-ivory hover:bg-ivory/5"}`}>
                <Icon size={17} strokeWidth={1.6} /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-5 border-t border-ivory/10 space-y-2">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-ivory/60 hover:text-ivory" data-testid="admin-view-site">
            <ExternalLink size={14} /> View live site
          </a>
          <div className="text-[0.65rem] text-ivory/40 truncate">{user?.email}</div>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="flex items-center gap-2 text-xs text-ivory/60 hover:text-ivory" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 ml-64">
        <header className="bg-ivory border-b border-espresso/10 px-10 py-7 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="font-serif text-3xl text-espresso">{title}</h1>
            {subtitle && <p className="text-sm text-taupe mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">{actions}</div>
        </header>
        <div className="p-10">{children}</div>
      </div>
    </div>
  );
}

export function SaveButton({ onClick, saving, label = "Save Changes" }) {
  return (
    <button onClick={onClick} disabled={saving} data-testid="admin-save-button"
      className="bg-espresso text-ivory px-7 py-3 rounded-full text-xs uppercase tracking-[0.16em] hover:bg-espressosoft transition-all disabled:opacity-60">
      {saving ? "Saving…" : label}
    </button>
  );
}
